import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { buildAppMeta } from "@/lib/seo";
import {
  type PublicClinic,
  PAYMENT_MODE_LABELS,
  fmtClinicPrice,
  manageClinicRegistrationUrl,
} from "@/lib/clinics";
import { fmtBookingDate, fmtSlotTime, googleCalendarUrl, icsFileContent } from "@/lib/booking";
import { submitClinicRegistration, type RegisterClinicResult } from "@/lib/clinics.functions";
import { CalendarCheck2, Check, Clock, GraduationCap, Loader2, MapPin, Users } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/clinic/$token")({
  head: () => ({
    meta: buildAppMeta({
      title: "Join a clinic",
      description: "Reserve a spot in a group clinic.",
      path: "/clinic",
    }),
  }),
  component: PublicClinicPage,
});

const ERROR_COPY: Record<string, string> = {
  not_found: "This clinic page is no longer active.",
  clinic_started: "This clinic has already started.",
  already_registered: "You're already signed up for this clinic with that email.",
  too_many_requests: "Too many attempts. Please wait a few minutes and try again.",
  invalid_input: "Please check your name and email and try again.",
  // Both codes read the same to a registrant on purpose — the coach's billing
  // status is never exposed publicly. The real reason surfaces in-app only.
  payments_unavailable:
    "Online payment isn't available for this clinic — please contact your coach to arrange payment.",
  subscription_required:
    "Online payment isn't available for this clinic — please contact your coach to arrange payment.",
  server_error: "Something went wrong. Please try again.",
};

function PublicClinicPage() {
  const { token } = Route.useParams();
  const submit = useServerFn(submitClinicRegistration);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState<Extract<RegisterClinicResult, { ok: true }> | null>(
    null,
  );

  const pageQ = useQuery({
    queryKey: ["public-clinic", token],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_clinic", { _token: token });
      if (error) throw error;
      return (data as unknown as PublicClinic | null) ?? null;
    },
    staleTime: 60 * 1000,
    retry: 1,
  });

  const page = pageQ.data;
  const clinic = page?.clinic;
  const coachName = page?.coach?.business_name || page?.coach?.full_name || "Your coach";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await submit({
        data: {
          token,
          name,
          email,
          phone: phone || undefined,
          note: note || undefined,
          origin: window.location.origin,
          website,
        },
      });
      if (res.ok) {
        if (res.checkoutUrl) {
          // pay_online: hand straight off to Stripe Checkout to claim the spot.
          window.location.href = res.checkoutUrl;
          return;
        }
        setConfirmed(res);
        pageQ.refetch();
      } else {
        toast.error(ERROR_COPY[res.error] ?? ERROR_COPY.server_error);
      }
    } catch {
      toast.error(ERROR_COPY.server_error);
    } finally {
      setBusy(false);
    }
  }

  function downloadIcs() {
    if (!confirmed || !clinic) return;
    const c = confirmed.clinic;
    const ics = icsFileContent({
      date: c.date,
      start: c.start,
      end: c.end,
      title: `${c.title} with ${coachName}`,
      location: page?.location
        ? [page.location.name, page.location.city].filter(Boolean).join(", ")
        : undefined,
      uid: confirmed.registration.id,
    });
    const blob = new Blob([ics], { type: "text/calendar" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "clinic.ics";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (pageQ.isLoading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </Shell>
    );
  }

  if (!page || !clinic) {
    return (
      <Shell>
        <Card className="p-8 text-center">
          <GraduationCap className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">
            This clinic page isn't available. Check the link, or contact your coach.
          </p>
        </Card>
      </Shell>
    );
  }

  const full = clinic.spots_left <= 0;
  const priceLabel =
    clinic.price_cents > 0 ? fmtClinicPrice(clinic.price_cents, clinic.currency) : "Free";

  // ------------------------------------------------------ confirmation state
  if (confirmed) {
    const status = confirmed.registration.status;
    return (
      <Shell>
        <Card className="p-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
            <Check className="h-6 w-6 text-success" />
          </div>
          <h1 className="mt-3 text-xl font-bold">
            {status === "registered"
              ? "You're in!"
              : status === "waitlisted"
                ? "You're on the waitlist"
                : "Spot held — complete payment"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {status === "registered" &&
              `See you at ${clinic.title} on ${fmtBookingDate(confirmed.clinic.date)}. A confirmation email is on its way.`}
            {status === "waitlisted" &&
              "The clinic is currently full. If a spot opens up, we'll email you right away."}
            {status === "pending_payment" &&
              "Check your email for a payment link to lock in your spot."}
          </p>
          {status === "registered" && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <Button size="sm" variant="outline" asChild>
                <a
                  href={googleCalendarUrl({
                    date: confirmed.clinic.date,
                    start: confirmed.clinic.start,
                    end: confirmed.clinic.end,
                    title: `${clinic.title} with ${coachName}`,
                    timezone: confirmed.clinic.timezone,
                  })}
                  target="_blank"
                  rel="noreferrer"
                >
                  <CalendarCheck2 className="h-3.5 w-3.5 mr-1" /> Google Calendar
                </a>
              </Button>
              <Button size="sm" variant="outline" onClick={downloadIcs}>
                Download .ics
              </Button>
            </div>
          )}
          <p className="mt-4 text-xs text-muted-foreground">
            Plans change?{" "}
            <a
              className="underline"
              href={manageClinicRegistrationUrl(confirmed.registration.manage_token)}
            >
              Manage your registration
            </a>
            .
          </p>
        </Card>
      </Shell>
    );
  }

  // ---------------------------------------------------------- signup state
  return (
    <Shell>
      <Card className="p-6">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {coachName}
        </p>
        <h1 className="mt-1 text-2xl font-bold">{clinic.title}</h1>
        {clinic.skill_level && (
          <p className="mt-0.5 text-xs font-medium text-brand-text">{clinic.skill_level}</p>
        )}

        <div className="mt-4 space-y-1.5 text-sm">
          <p className="flex items-center gap-2">
            <CalendarCheck2 className="h-4 w-4 text-muted-foreground" />
            {fmtBookingDate(clinic.clinic_date)}
          </p>
          <p className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            {fmtSlotTime(clinic.start_time)} – {fmtSlotTime(clinic.end_time)}
          </p>
          {page.location && (
            <p className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              {page.location.name}
              {page.location.city
                ? ` — ${[page.location.city, page.location.state].filter(Boolean).join(", ")}`
                : ""}
            </p>
          )}
          <p className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            {full
              ? `Full — ${clinic.waitlist_count > 0 ? `${clinic.waitlist_count} on the waitlist` : "waitlist open"}`
              : `${clinic.spots_left} ${clinic.spots_left === 1 ? "spot" : "spots"} left`}
          </p>
        </div>

        {clinic.description && (
          <p className="mt-4 whitespace-pre-wrap text-sm text-muted-foreground">
            {clinic.description}
          </p>
        )}

        <div className="mt-4 rounded-md bg-muted/50 px-3 py-2 text-sm">
          <span className="font-semibold">{priceLabel}</span>
          {clinic.price_cents > 0 && (
            <span className="text-muted-foreground">
              {" "}
              · {PAYMENT_MODE_LABELS[clinic.payment_mode]}
            </span>
          )}
        </div>

        {clinic.status === "cancelled" ? (
          <p className="mt-5 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            This clinic has been cancelled. Contact {coachName} with any questions.
          </p>
        ) : (
          <form onSubmit={onSubmit} className="mt-5 space-y-3">
            <div>
              <Label htmlFor="cl-name" className="text-xs">
                Name
              </Label>
              <Input
                id="cl-name"
                value={name}
                required
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="cl-email" className="text-xs">
                Email
              </Label>
              <Input
                id="cl-email"
                type="email"
                value={email}
                required
                maxLength={200}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="cl-phone" className="text-xs">
                Phone (optional)
              </Label>
              <Input
                id="cl-phone"
                type="tel"
                value={phone}
                maxLength={40}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="cl-note" className="text-xs">
                Note for the coach (optional)
              </Label>
              <Textarea
                id="cl-note"
                value={note}
                rows={2}
                maxLength={1000}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            {/* Honeypot — hidden from humans */}
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <Button type="submit" className="w-full" disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {full ? "Join the waitlist" : "Reserve my spot"}
            </Button>
            {full && (
              <p className="text-center text-xs text-muted-foreground">
                We'll email you if a spot opens up.
              </p>
            )}
          </form>
        )}
      </Card>
      <p className="mt-4 text-center text-xs text-muted-foreground">Powered by CoachQuick</p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto w-full max-w-md">{children}</div>
    </div>
  );
}
